import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';

import { VOApi } from '../api';
import { Q_VO, M_CREATE_CREDIT_LINE_REQUEST, M_UPDATE_CREDIT_LINE_REQUEST } from '../../../queries/common';
import { UpdateVOResponse, UpdateVOVariables } from '../api/types';
import { CreateCreditLineRequestInput, UpdateCreditLineRequestInput } from '@trnsact/trnsact-shared-types';

export function useVO() {
  const { showNotification } = useNotifications();

  const [onUpdateVO, { loading: isVOUpdating }] = useMutation<UpdateVOResponse, UpdateVOVariables>(VOApi.update, {
    awaitRefetchQueries: true,
  });

  const [onCreateCreditLineRequest] = useMutation(M_CREATE_CREDIT_LINE_REQUEST, {
    awaitRefetchQueries: true,
    refetchQueries: [Q_VO],
  });

  const [onUpdateCreditLineRequest] = useMutation(M_UPDATE_CREDIT_LINE_REQUEST, {
    awaitRefetchQueries: true,
    refetchQueries: [Q_VO],
  });

  const handleUpdateVO = useCallback(
    async (variables: UpdateVOVariables) => {
      try {
        await onUpdateVO({ variables });
      } catch (error) {
        logError(error);
      }
    },
    [onUpdateVO]
  );

  const [onUpdateCurrentStage, { loading: isVOStageUpdating }] = useMutation(VOApi.updateCurrentStage, {
    awaitRefetchQueries: true,
    refetchQueries: [Q_VO],
  });

  const handleUpdateCurrentStage = useCallback(
    async (variables: { voId: string; flowId: string; stageId: string }) => {
      try {
        const { data, errors } = await onUpdateCurrentStage({ variables: { input: variables } });

        if (!data?.updateVOCurrentStage?.success || errors?.length) {
          showNotification('Something went wrong', { type: 'error' });
        } else {
          showNotification('Stage successfully updated!');
        }
      } catch (error) {
        logError(error);
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [onUpdateCurrentStage, showNotification]
  );

  const createCreditLineRequest = useCallback(async (data: CreateCreditLineRequestInput) => {
    try {
      await onCreateCreditLineRequest({ variables: { input: data } });
      showNotification('Credit line successfully created!');
    } catch (error) {
      logError(error);
      showNotification('Creation of credit line failed!', { type: 'error' });
    }
  }, []);

  const updateCreditLineRequest = useCallback(async (data: UpdateCreditLineRequestInput) => {
    try {
      await onUpdateCreditLineRequest({ variables: { input: data } });
      showNotification('Credit line successfully updated!');
    } catch (error) {
      logError(error);
      showNotification('Update of credit line failed!', { type: 'error' });
    }
  }, []);

  return {
    isVOUpdating,
    handleUpdateVO,
    isVOStageUpdating,
    handleUpdateCurrentStage,
    createCreditLineRequest,
    updateCreditLineRequest,
  };
}
